<script setup>
import {useAttrs} from "vue";
import {ref, watch, onMounted, nextTick} from 'vue';
import DefaultLabel from "@/components/global-themed/inputs/DefaultLabel.vue";

const props = defineProps({
  validationErrors: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  label: {
    type: [String, null],
    required: false,
    default: null,
  },
  resizeable: {
    type: Boolean,
    default: false,
  },
  iconPath: {
    type: [String, null],
    required: false,
    default: null,
  },
  autoExpand: {
    type: Boolean,
    default: false,
  },
});

defineOptions({
  inheritAttrs: false
});

const {class: classAttribute, ...inputAttributes} = useAttrs();

const textareaValue = defineModel({
  type: [String],
  required: true,
});

const textareaRef = ref(null);

function resizeTextarea() {
  if (props.autoExpand && textareaRef.value) {
    textareaRef.value.style.height = 'auto';
    textareaRef.value.style.height = textareaRef.value.scrollHeight + 'px';
  }
}

onMounted(() => {
  if (props.autoExpand) {
    nextTick(resizeTextarea);
  }
});

watch(textareaValue, () => {
  if (props.autoExpand) {
    nextTick(resizeTextarea);
  }
}, {immediate: true});
</script>

<template>
  <div>
    <div class="tw-relative tw-w-full">
      <div class="tw-flex tw-flex-col tw-w-full">
        <DefaultLabel
          v-if="label"
          :for="$attrs.id"
          :label="label"
        />
        <div
          v-bind="classAttribute"
          class="tw-flex tw-gap-3 tw-py-4 focus-within:tw-border-primary tw-border-neutral tw-border tw-text-neutral-900 tw-self-stretch tw-px-6 tw-text-base tw-w-full tw-rounded-lg"
          :class="[
            autoExpand ? 'tw-min-h-13' : 'tw-h-64',
            {'!tw-border-danger': validationErrors.length > 0}
          ]"
        >
          <div
            v-if="iconPath"
            class="tw-flex tw-justify-center tw-items-start"
          >
            <svg-vue
              :icon="iconPath"
              class="tw-h-6 tw-w-6 tw-text-neutral"
            />
          </div>

          <textarea
            ref="textareaRef"
            v-model="textareaValue"
            v-bind="inputAttributes"
            class="tw-flex-grow"
            :class="[
              resizeable ? 'tw-resize-y' : 'tw-resize-none',
              autoExpand ? 'tw-overflow-hidden' : ''
            ]"
            type="text"
            :rows="autoExpand ? 1 : inputAttributes.rows"
            @input="resizeTextarea"
          />
        </div>
      </div>

      <small
        v-for="(error, index) in validationErrors"
        :key="index"
        class="tw-text-danger tw-self-baseline tw-absolute tw-block tw-mt-1"
      >{{ error }}</small>
    </div>
  </div>
</template>
